import { Skeleton } from '@/shared/components/ui/skeleton';
import { ChartContainer } from '../../analytics/shared/ChartContainer';
import { EmptyState } from '../../analytics/shared/EmptyState';
import { ErrorState } from '../../analytics/shared/ErrorState';
import { useReferralFeedbackData } from '../../../hooks/useReferralFeedbackData';
import { ReferralFeedbackSpTable } from '../referral-feedback/ReferralFeedbackSpTable';
import { ReferralFeedbackCsTable } from '../referral-feedback/ReferralFeedbackCsTable';
import type { ReferralFeedbackReason } from '../../../types/engagement-metrics.types';

type EngagementMetricsReferralFeedbackTabProps = {
  isActive?: boolean;
};

const CONVERTED_LABELS: Record<string, string> = {
  YES: 'Converted',
  NOT_YET: 'Not yet',
  NO: 'Did not convert',
};

const convertedLabel = (converted: string | null): string =>
  (converted && CONVERTED_LABELS[converted]) || '—';

const ReasonsList = ({ reasons }: { reasons: ReferralFeedbackReason[] }) => {
  if (reasons.length === 0) {
    return <p className="text-sm text-gray-500">No reasons shared yet.</p>;
  }
  return (
    <ul className="space-y-4">
      {reasons.map((item, i) => (
        <li key={i} className="border-b last:border-0 pb-4 last:pb-0" style={{ borderColor: '#edeae3' }}>
          <div className="flex flex-wrap items-baseline gap-x-2 mb-1">
            <span className="text-[13px] font-semibold" style={{ color: '#1b2a23' }}>
              {item.spName}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-[0.3px]" style={{ color: '#7f8a83' }}>
              {convertedLabel(item.converted)}
            </span>
            <span className="text-[11px]" style={{ color: '#7f8a83' }}>
              {new Date(item.respondedAt).toLocaleDateString()}
            </span>
          </div>
          <p className="text-[12.5px]" style={{ color: '#46584f' }}>
            {item.reason}
          </p>
        </li>
      ))}
    </ul>
  );
};

/**
 * Feature 102 slice 4 — Referral Feedback sub-tab: per-SP reach/conversion/rating
 * summary (rows expand into a per-referral drill-down), care-seeker feedback per
 * SP, and the admin-only "why it didn't convert" reasons. Unfiltered endpoint,
 * so this tab ignores the shared date-range control bar.
 */
export const EngagementMetricsReferralFeedbackTab = ({
  isActive = true,
}: EngagementMetricsReferralFeedbackTabProps) => {
  const { data, isLoading, isError, refetch } = useReferralFeedbackData(isActive);

  if (!isActive) return null;

  if (isError) {
    return (
      <div className="pt-6" data-testid="engagement-metrics-referral-feedback-tab">
        <ErrorState
          message="Failed to load referral feedback. Please try again."
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6 pt-6" data-testid="engagement-metrics-referral-feedback-tab">
        <Skeleton className="h-80 w-full rounded-xl" />
        <Skeleton className="h-52 w-full rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6" data-testid="engagement-metrics-referral-feedback-tab">
      <ChartContainer
        title="Referral Feedback by Provider"
        subtitle="Reach, conversion, and ratings from day-30 SP follow-ups — click a row for per-referral detail"
      >
        {data.spSummary.length === 0 ? (
          <EmptyState message="No follow-up responses yet." />
        ) : (
          <ReferralFeedbackSpTable rows={data.spSummary} />
        )}
      </ChartContainer>

      <ChartContainer
        title="Care-Seeker Feedback by Provider"
        subtitle="Connection outcomes and ratings reported by care seekers — independent of SP follow-ups"
      >
        <ReferralFeedbackCsTable rows={data.csSummary} />
      </ChartContainer>

      <ChartContainer
        title="Referral feedback from SPs"
        subtitle="Free-text outcome notes from SPs — visible to the bQuest team only"
      >
        <ReasonsList reasons={data.reasons} />
      </ChartContainer>
    </div>
  );
};
